import type { JSX } from "react";
import type { ButtonBaseProps, FabProps } from "@mui/material";
import type { ValueOrI18nKeyProp } from "../../util/type";
import type { IconComponent } from "./icons/icon-component";
import { ButtonBase, CircularProgress, Fab, Tooltip, css, styled } from "@mui/material";
import classNames from "classnames";
import { useServices } from "../../services/services-context";
import { CentredSpinner } from "./centred-spinner";


export type ToolButtonColor = (
	| "primary"
	| "secondary"
	| "danger"
);
export type ToolButtonProps = Omit<ButtonBaseProps, "children"> & ValueOrI18nKeyProp<"label", string> & {
	href?: string | undefined | null;
	icon: IconComponent;
	color?: ToolButtonColor;
	floating?: boolean;
	fullLabel?: boolean;
	round?: boolean;
	loading?: boolean;
};

const ToolButtonRoot = styled(ButtonBase)(({ theme }) => css`
	width: ${theme.layout.targetSize};
	height: ${theme.layout.targetSize};
	background-color: var(--toolButton-background-color, transparent);
	color: var(--toolButton-color, inherit);

	&.primary {
		--toolButton-background-color: ${theme.palette.primary.main};
		--toolButton-color: ${theme.palette.primary.main};

		&:disabled {
			--toolButton-background-color: transparent;
		}

		color: ${theme.palette.primary.contrastText};
		--hover-color: ${theme.palette.primary.light};
	}

	&:disabled {
		opacity: .5;
	}

	&.round {
		border-radius: 50%;
	}

	&.danger {
		--toolButton-color: ${theme.palette.error.light};

		&:disabled {
			--toolButton-color: ${theme.palette.error.dark};
			opacity: .5;
		}
	}

	&.fullLabel {
		width: fit-content;
		padding-left: ${theme.spacing(1)};

		.fullLabelText {
			font-size: ${theme.typography.button.fontSize};
			padding: ${theme.spacing(0, 1)};
		}
	}
`);

const FloatingToolButtonRoot = styled(Fab)(({ theme }) => css`
	${theme.breakpoints.up("md")} {
		width: 32px;
		height: 32px;
		min-height: 0px;
		svg {
			width: 18px;
			height: 18px;
		}
	}
	 z-index: 0;
`);

const getFabColor = (color: ToolButtonColor): FabProps["color"] => {
	switch(color) {
		case "danger":
			return "error";
		case "secondary":
			return "inherit";
		default:
			return color;
	}
};

export const ToolButton = ({ className, label, labelKey, labelValues, icon: Icon, color = "secondary", floating, fullLabel, round, loading, href, ...buttonBaseProps }: ToolButtonProps): JSX.Element => {
	const { translation } = useServices();

	const labelValue = translation.resolveValueOrI18nKey(label, labelKey, labelValues) as string;

	if(loading) {
		return (
			<CircularProgress
				className={ className }
				size={ 24 }
				sx={{ marginInlineEnd: 1 }}
			/>
		);
	}

	const button = floating
		? (
			<FloatingToolButtonRoot
				className={ className }
				aria-label={ labelValue }
				color={ getFabColor(color) }
				href={ href ?? undefined }
				{ ...buttonBaseProps }
				disabled={ buttonBaseProps.disabled || loading }
			>
				<Icon/>
				{ loading && <CentredSpinner/> }
			</FloatingToolButtonRoot>
		) : (
			<ToolButtonRoot
				className={ classNames(color, { floating, round, fullLabel }, className) }
				{ ...buttonBaseProps }
				aria-label={ labelValue }
			>
				<Icon/>
				{ fullLabel && <span className="fullLabelText">{ labelValue }</span> }
			</ToolButtonRoot>
		);

	if(buttonBaseProps.disabled) {
		return button;
	}

	return (
		<Tooltip
			title={ labelValue }
			describeChild
			disableInteractive
		>
			{ button }
		</Tooltip>
	);
};